import React, { useEffect, useState } from "react";
import ErrorText from "../commonComponents/ErrorTextContainer";
import Textfield from "../InviteUserModal/Textfield";
import "./CustomCreditModal.css";
import { handleAddCustomPlanCredits } from "../../utils/billing";
import { useParams } from "react-router-dom";
import { notify, uploadFileToCloudfare } from "../../utils/utils";
import "./InvoiceUploadModal.css";
import {
  CENTRALISED_BILLING_API,
  UNIFIED_DASHBOARD_URL,
} from "../../utils/envConfig";
import { useSelector } from "react-redux";

function VerticalDivider() {
  return (
    <div className="h-8 relative shrink-0 w-0" data-name="Divider">
      <div className="absolute bottom-[-1.563%] left-[-0.5px] right-[-0.5px] top-[-1.563%]">
        <svg
          className="block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 2 34"
        >
          <g id="Divider">
            <path
              d="M1 1V33"
              id="V Divider"
              stroke="var(--stroke-0, #EAE7EC)"
              strokeLinecap="round"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="block cursor-pointer relative shrink-0 size-6 hover:opacity-70 transition-opacity"
      data-name="Close"
      onClick={onClick}
    >
      <svg
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 24 24"
      >
        <g id="Close">
          <path
            d="M18 6L6 18M6 6L18 18"
            id="Icon"
            stroke="var(--stroke-0, #211F26)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </g>
      </svg>
    </button>
  );
}

export default function InvoiceUploadModal({
  onClose,
  invoiceTableData,
  uploadedInvoice,
}: {
  onClose: () => void;
  invoiceTableData: any[];
  uploadedInvoice: () => void;
}) {
  const pdfInputRef = React.useRef<HTMLInputElement>(null);
  const params = useParams();
  const globalReducer = useSelector((state: any) => state.globalReducer);

  const [loading, setLoading] = useState(false);

  const [allInvoices, setAllInvoices] = useState<any[]>([]);

  const [invoiceId, setInvoiceId] = useState("");
  const [invoiceIdError, setInvoiceIdError] = useState("");
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [inputFileUrl, setInputFileUrl] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  useEffect(() => {
    if (invoiceTableData && invoiceTableData.length > 0) {
      const offlineInvoices = invoiceTableData.filter(
        (invoice) => invoice.isOfflineInvoice
      );

      setAllInvoices(offlineInvoices);
    }
  }, [invoiceTableData]);

  useEffect(() => {
    return () => {
      setLoading(false);
      setInvoiceId("");
      setInvoiceIdError("");
      setAmount("");
      setAmountError("");
      setInputFileUrl("");
      setSelectedInvoice(null);
      setUploadedFile(null);
    };
  }, []);

  useEffect(() => {
    if (allInvoices.length > 0) {
      setInvoiceId(allInvoices[0].orderNumber);
      setAmount(allInvoices[0].amountPaid.toString());
      setSelectedInvoice(allInvoices[0]);
    }
  }, [allInvoices]);

  const handleUploadPDF = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadedFile(file || null);

    if (!file) {
      notify("No file selected", "error");
      return;
    }

    try {
      setUploadingPdf(true);
      const fileUrl = await uploadFileToCloudfare(file);
      console.log("fileUrl", fileUrl);
      setInputFileUrl(fileUrl);
      setUploadingPdf(false);
      notify("File uploaded successfully", "success");
    } catch (err) {
      console.error("Error uploading PDF:", err);
      setUploadingPdf(false);
      notify("Failed to upload file", "error");
    }
  };

  const handleUploadPdf = async () => {
    setLoading(true);

    if (!invoiceId) {
      setInvoiceIdError("Please select an invoice");
      setLoading(false);
      return;
    } else {
      setInvoiceIdError("");
    }

    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setAmountError("Please enter a valid amount");
      setLoading(false);
      return;
    } else {
      setAmountError("");
    }

    if (!inputFileUrl) {
      notify("Please upload a PDF file", "error");
      setLoading(false);
      return;
    }

    try {
      const workspaceId = params.workspaceId;
      const bodyData = {
        workspaceId: workspaceId,
        invoiceUrl: inputFileUrl,
        amount: parseInt(amount, 10),
        invoiceId: invoiceId,
        creditBlockId: selectedInvoice?.creditBlockId,
        currency: selectedInvoice?.currency || "INR",
        addedBy: globalReducer.userData._id,
      };

      console.log("bodyData", bodyData);

      const response = await fetch(
        `${CENTRALISED_BILLING_API}/invoice/${workspaceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${globalReducer.token}`,
          },
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload invoice");
      }

      const data = await response.json();
      notify("Invoice uploaded successfully", "success");
      uploadedInvoice();
      onClose();
    } catch (error) {
      console.error("Error uploading invoice:", error);
      notify("Error uploading invoice", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading || uploadingPdf) {
    return (
      <div className="relative h-screen w-full" data-name="Invite User Modal">
        <div
          className="absolute box-border content-stretch flex flex-col gap-2 items-center justify-end pb-0 pt-14 pl-14 pr-14 top-0 w-[1000px]"
          data-name="Modal"
          style={{
            paddingLeft: "56px",
            paddingRight: "56px",
            paddingTop: "56px",
            right: "calc(50% - 500px)",
            maxHeight: "90vh",
            minHeight: "350px",
          }}
        >
          <div
            className="backdrop-blur-md backdrop-filter basis-0 bg-[rgba(255,255,255,0.72)] grow min-h-px min-w-px relative rounded-[24px] shadow-[-4px_0px_24px_0px_rgba(0,0,0,0.02)] shrink-0 w-full flex items-center justify-center"
            data-name="Container"
          >
            {uploadingPdf ? "Uploading PDF..." : "Loading..."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-full" data-name="Invite User Modal">
      <div
        className="absolute box-border content-stretch flex flex-col gap-2 items-center justify-end pb-0 pt-14 pl-14 pr-14 top-0 w-[1000px]"
        data-name="Modal"
        style={{
          paddingLeft: "56px",
          paddingRight: "56px",
          paddingTop: "56px",
          right: "calc(50% - 500px)",
          maxHeight: "90vh",
          minHeight: "350px",
        }}
      >
        <div
          className="backdrop-blur-md backdrop-filter basis-0 bg-[rgba(255,255,255,0.72)] grow min-h-px min-w-px relative rounded-[24px] shadow-[-4px_0px_24px_0px_rgba(0,0,0,0.02)] shrink-0 w-full"
          data-name="Container"
        >
          <div className="overflow-clip relative size-full flex flex-col">
            <div
              className="box-border content-stretch flex flex-row gap-6 items-center justify-center p-0 relative shrink-0 w-full p-[24px]"
              data-name="Header"
            >
              <div
                className="basis-0 box-border content-stretch flex flex-row gap-6 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0"
                data-name="Header"
              >
                <div className="font-['Geist:Medium',_sans-serif] font-medium leading-[0] relative shrink-0 text-[#211f26] text-[24px] text-left text-nowrap tracking-[-0.24px]">
                  <p className="adjustLetterSpacing block leading-[30px] whitespace-pre">
                    {"Upload Invoice"}
                  </p>
                </div>
                <VerticalDivider />
              </div>
              <CloseButton onClick={onClose} />
            </div>
            <div className="custom-credit-body">
              <div className="custom-credit-input">
                <div className="input-tab-invoice">
                  <label>Order Number</label>
                  <select
                    value={invoiceId}
                    onChange={(e) => {
                      const selected = allInvoices.find(
                        (invoice) => invoice.orderNumber === e.target.value
                      );
                      console.log("selected", selected);
                      setInvoiceId(selected ? selected.orderNumber : "");
                      setAmount(selected ? selected.amountPaid.toString() : "");
                      setSelectedInvoice(selected || null);
                    }}
                    className="custom-credit-select"
                  >
                    {allInvoices.map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.orderNumber}
                      </option>
                    ))}
                  </select>
                  {invoiceIdError && (
                    <ErrorText hasError={invoiceIdError}></ErrorText>
                  )}
                </div>

                <div className="input-tab-invoice">
                  <Textfield
                    label="Amount"
                    value={amount}
                    onChange={(value) => setAmount(value)}
                    placeholder="10000"
                    type="number"
                  />
                  {amountError && (
                    <ErrorText hasError={amountError}></ErrorText>
                  )}
                </div>
                <div className="input-tab-invoice upload-file-btn">
                  {<p className="file-name">{uploadedFile?.name || ""}</p>}
                  <button
                    className={`border-[#eae7ec] border-[0.2px] border-solid min-w-px relative rounded-lg  cursor-pointer hover:bg-[#f5f3f6] transition-colors px-[12px] py-[8px]`}
                    data-name="_segmented-control-item"
                    onClick={(e) => {
                      pdfInputRef.current?.click();
                    }}
                  >
                    <p className="block leading-[20px] whitespace-pre">
                      Choose File
                    </p>
                  </button>{" "}
                  <input
                    type="file"
                    ref={pdfInputRef}
                    accept=".pdf"
                    onChange={handleUploadPDF}
                    style={{ display: "none" }}
                  ></input>
                </div>
              </div>
            </div>
            <div className="custom-credit-footer">
              <button
                className="add-custom-credit"
                onClick={() => {
                  handleUploadPdf();
                }}
                style={{}}
              >
                {"Upload Invoice"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
